import { Field, Input, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useController, Control, FieldValues } from "react-hook-form";
import { InputGroup } from "../ui/input-group";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { useEmailValidationStore } from "@/store/emailValidationStore";
import { isEmail } from "@/utils/reg-expressions";

type DebouncedInputProps = {
  name: string;
  control: Control<FieldValues>;
  debounce?: number;
  placeholder?: string;
  height?: string;
  minHeight?: string;
  required?: boolean;
  maxLength?: number;
  labelText?: string;
  disabled?: boolean;
  validate?: boolean;
};

export const EmailValidationInput = ({
  name,
  control,
  debounce = 500,
  placeholder,
  height,
  maxLength,
  minHeight,
  required,
  labelText,
  disabled,
  validate,
}: DebouncedInputProps) => {
  const {
    field: { onChange, value },
  } = useController({ name, control });

  const [error, setError] = useState<{ message: string } | null>(null);

  const [localValue, setLocalValue] = useState(value ?? "");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { setValidation } = useEmailValidationStore();

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  useEffect(() => {
    setError(null);
  }, [localValue]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!validate) return;
      setError(null);
      onChange(localValue);

      if (isEmpty(localValue)) {
        setError(null);
        setValidation({
          error: null,
          isValid: true,
        });
        return;
      }
      if (isEmail.test(localValue)) {
        setError(null);
        setValidation({
          error: null,
          isValid: true,
        });
      } else {
        setError({
          message: "Debe ser un email válido",
        });
        setValidation({
          error: "Debe ser un email válido",
          isValid: false,
        });
      }
    }, debounce);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localValue]);

  return (
    <Field.Root invalid={!!error} required={required}>
      <Field.Label>
        <Text fontWeight={600}>{labelText}</Text>
        {required && (
          <Text fontWeight={600} color={AppColors.REQUIRED}>
            *
          </Text>
        )}
      </Field.Label>
      <InputGroup
        width="100%"
        flex="1"
        _placeholder={{
          color: AppColors.LIGHT_GRAY_TEXT,
        }}
      >
        <Input
          value={value}
          placeholder={placeholder}
          height={height}
          minH={minHeight}
          fontSize="14px"
          paddingX="12px"
          disabled={disabled}
          maxLength={maxLength}
          onChange={onChange}
          data-testid="dni-validation-input"
        />
      </InputGroup>
      {error && <Field.ErrorText>{String(error?.message)}</Field.ErrorText>}
    </Field.Root>
  );
};
